import Interaction from 'ol/interaction/Interaction'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import CustomDraw from './CustomDraw'
import CustomDrawTouch from './CustomDrawTouch'
import ControlCreateTools from '../control/ControlCreateTools'
import { unByKey } from 'ol/Observable'
import {
  Circle as CircleStyle,
  Fill,
  RegularShape,
  Stroke,
  Style,
  Text,
} from 'ol/style'
import { getArea, getLength } from 'ol/sphere'
import { LineString, Point } from 'ol/geom'

const layerName = '__kmapv_interaction_measure'

const style = new Style({
  fill: new Fill({
    color: 'rgba(255, 255, 255, 0.2)',
  }),
  stroke: new Stroke({
    color: 'rgba(0, 0, 0, 0.5)',
    lineDash: [10, 10],
    width: 2,
  }),
  image: new CircleStyle({
    radius: 5,
    stroke: new Stroke({
      color: 'rgba(0, 0, 0, 0.7)',
    }),
    fill: new Fill({
      color: 'rgba(255, 255, 255, 0.2)',
    }),
  }),
})

const labelStyle = new Style({
  text: new Text({
    font: '14px Calibri,sans-serif',
    fill: new Fill({
      color: 'rgba(255, 255, 255, 1)',
    }),
    backgroundFill: new Fill({
      color: '#4c4c4c',
    }),
    padding: [3, 3, 3, 3],
    textBaseline: 'bottom',
    offsetY: -15,
  }),
  image: new RegularShape({
    radius: 8,
    points: 3,
    angle: Math.PI,
    displacement: [0, 10],
    fill: new Fill({
      color: '#4c4c4c',
    }),
  }),
})

const tipStyle = new Style({
  text: new Text({
    font: '12px Calibri,sans-serif',
    fill: new Fill({
      color: 'rgba(255, 255, 255, 1)',
    }),
    backgroundFill: new Fill({
      color: 'rgba(0, 0, 0, 0.4)',
    }),
    padding: [2, 2, 2, 2],
    textAlign: 'left',
    offsetX: 15,
  }),
})

const segmentStyle = new Style({
  text: new Text({
    font: '12px Calibri,sans-serif',
    fill: new Fill({
      color: 'rgba(255, 255, 255, 1)',
    }),
    backgroundFill: new Fill({
      color: '#999999',
    }),
    padding: [2, 2, 2, 2],
    textBaseline: 'bottom',
    offsetY: -12,
  }),
  image: new RegularShape({
    radius: 6,
    points: 3,
    angle: Math.PI,
    displacement: [0, 8],
    fill: new Fill({
      color: '#999999',
    }),
  }),
})

const segmentStyles = [segmentStyle]

const formatLength = function (line) {
  const length = getLength(line)
  if (length === 0) return null
  let output
  if (length > 100) {
    output = Math.round((length / 1000) * 100) / 100 + ' km'
  } else {
    output = Math.round(length * 100) / 100 + ' m'
  }
  return output
}

const formatArea = function (polygon) {
  const area = getArea(polygon)
  if (area === 0) return null
  let output
  if (area > 10000) {
    output = Math.round((area / 1000000) * 100) / 100 + ' km\xB2'
  } else {
    output = Math.round(area * 100) / 100 + ' m\xB2'
  }
  return output
}

function styleFunction (feature, segments, drawLabel, drawType, tip) {
  if (!feature) { return null }
  const styles = [style]
  const geometry = feature.getGeometry()
  if (!geometry) { return null }
  const type = geometry.getType()
  let point, label, line
  if (!drawType || drawType === type) {
    if (type === 'Polygon') {
      point = geometry.getInteriorPoint()
      label = formatArea(geometry)
      line = new LineString(geometry.getCoordinates()[0])
    } else if (type === 'LineString') {
      point = new Point(geometry.getLastCoordinate())
      label = formatLength(geometry)
      line = geometry
    }
  }
  if (segments && line) {
    let count = 0
    line.forEachSegment(function (a, b) {
      const segment = new LineString([a, b])
      const label = formatLength(segment)
      if (label) {
        if (segmentStyles.length - 1 < count) {
          segmentStyles.push(segmentStyle.clone())
        }
        const segmentPoint = new Point(segment.getCoordinateAt(0.5))
        segmentStyles[count].setGeometry(segmentPoint)
        segmentStyles[count].getText().setText(label)
        styles.push(segmentStyles[count])
        count++
      }
    })
  }
  if (label && drawLabel) {
    labelStyle.setGeometry(point)
    labelStyle.getText().setText(label)
    styles.push(labelStyle)
  }
  if (
    tip &&
    type === 'Point' // &&
    //! modify.getOverlay().getSource().getFeatures().length
  ) {
    // tipPoint = geometry
    tipStyle.getText().setText(tip)
    styles.push(tipStyle)
  }
  return styles
}

class Measure extends Interaction {
  constructor (options) {
    options = options || {}

    // on utilise self car on ne peut pas utiliser this avant la méthode super()
    // const self = null

    super(options = options || {})

    this.mode = options.mode || 'length'

    this.viewer = options.viewer || null

    this.showSegments = options.showSegments || true

    // calque de dessin
    if (options.layer) {
      // on a fourni un layer sur lequel trianguler
      this.layer = options.layer
    } else {
      // sinon on le créer une seule fois dans l'appli
      if (this.viewer.commonLayer.layerExist(layerName, 'SYSTEM')) {
        // remet tjs le calque au dessus
        this.viewer.commonLayer.upLayer(layerName, 'SYSTEM')
        this.layer = this.viewer.commonLayer.getLayer(layerName, 'SYSTEM')
      } else {
        /**
        * @type {VectorLayer}
        */
        this.layer = new VectorLayer({
          [this.viewer.commonLayer.propertiesName.ID_LAYER]: layerName,
          source: new VectorSource(),
          style: function (feature) {
            return styleFunction(feature, this.showSegments, true)
          }.bind(this),
          displayInLayerSwitcher: false,
          zIndex: options.zIndex || 10000,
        })
        this.viewer.commonLayer.addLayer(this.layer, 'SYSTEM')
        this.viewer.getFeatureSnapper().addSource({ groups: [{ name: 'KMAPV', edge: true, vertex: true }], source: this.layer.getSource() })
      }
    }
  }

  /**
 * Remove the interaction from its current map, if any,  and attach it to a new
 * map, if any. Pass `null` to just remove the interaction from the current map.
 * @param {ol.Map} map Map.
 * @api stable
 */
  setMap (map) {
    super.setMap(map)
  }

  /** Activate / deactivate interaction
   * @param {boolean} b
   */
  setActive (active) {
    const wasActive = this.getActive()
    super.setActive(active)
    if (!active && wasActive) {
      removeInterraction.call(this, false)
    }
  }
}

// gestion des interaction
const removeInterraction = function (changeMapMode) {
  unByKey(this.onDrawAbortListener)
  unByKey(this.onDrawEndListener)
  unByKey(this.onDrawStartListener)
  // this.setVisible(true)
  if (this.createTool) {
    this.viewer.Map.removeControl(this.createTool)
  }
  if (this.drawInteraction) {
    this.drawInteraction.setActive(false)
    this.viewer.Map.removeInteraction(this.drawInteraction)
  }
  const currentMapModeInteractions = this.viewer.commonLayer.getCurrentMapMode()?.interactions || []

  if (currentMapModeInteractions.some(interaction => interaction === this)) {
    this.viewer.dataLayer.changeMapMode('select', this.toolId)
  }

  this.viewer.horizontalToolbar.setVisible(this.horizontalToolbarVisility)
  // this.horizontalToolbarVisility
  this.onDrawStartListener = null
  this.onDrawEndListener = null
  this.onDrawAbortListener = null
  this.createTool = null
  this.drawInteraction = null
}

const _buildMeasureObject = function (feature) {
  const geometry = feature.getGeometry()
  const measure = {
    length: {
      label: formatLength(geometry),
      value: getLength(geometry),
    },
  }

  if (feature.getGeometry().getType() === 'Polygon') {
    measure.area = {
      label: formatArea(geometry),
      value: getArea(geometry),
    }
  }
  return measure
}

Measure.prototype.startMeasurement = function () {
  this.viewer.commonLayer.upLayer(layerName, 'SYSTEM')
  // garde l'état d'origine de la barre horizontal
  this.horizontalToolbarVisility = this.viewer.horizontalToolbar.getVisible()
  this.viewer.horizontalToolbar.setVisible(false)

  const geometryType = this.mode === 'length' ? 'LineString' : 'Polygon'

  const activeTip =
    (this.viewer.isDesktop ? 'Cliquer' : 'Tap') +
    ' pour continuer de mesurer ' +
    (this.mode === 'length' ? 'la distance' : 'la surface')
  const idleTip = (this.viewer.isDesktop ? 'Cliquer' : 'Tap') + ' pour débuter la mesure'
  let tip = idleTip

  this.drawInteraction = this.viewer.isDesktop
    ? new CustomDraw({
      // active: false,
      type: geometryType,
      stopClick: true,
      viewer: this.viewer,
      style: function (feature) {
        return styleFunction(feature, this.showSegments, true, geometryType, tip)
      }.bind(this),
    })
    : new CustomDrawTouch({
      // active: false,
      type: geometryType,
      viewer: this.viewer,
      style: function (feature) {
        return styleFunction(feature, this.showSegments, true)
      }.bind(this),
      sketchStyle: function (feature) {
        return styleFunction(feature, this.showSegments, false, geometryType, tip)
      }.bind(this),
    })

  this.viewer.Map.addInteraction(this.drawInteraction)
  // on utilise l'event OL plutot que viewer car meilleure gestion du scope
  this.onDrawStartListener = this.drawInteraction.on('drawstart', () => {
    this.viewer.dispatchEvent('measurestart')
    tip = activeTip
  })

  this.onDrawEndListener = this.drawInteraction.on('drawend', (ev) => {
    this.layer.getSource().addFeature(ev.feature)
    this.viewer.dispatchEvent('measureend', {
      feature: ev.feature,
      measure: _buildMeasureObject(ev.feature),
    })
    // on remet en mapmode normal
    removeInterraction.call(this)
  })
  this.onDrawAbortListener = this.drawInteraction.on('drawabort', (ev) => {
    this.viewer.dispatchEvent('measureabort')
    // on remet en mapmode normal
    removeInterraction.call(this)
  })

  const createToolOptions = {
    // la boite d'outils de création ne fait qu'utiliser l'interaction drawtouch venant de datalayer, on fourni la bonne
    viewer: this.viewer,
    geometryType: this.mode === 'length' ? 'LineString' : 'Polygon',
    interaction: this.drawInteraction,
    title: 'Mesure de ' + (this.mode === 'length' ? 'distance' : 'surface'),
  }

  this.createTool = new ControlCreateTools(createToolOptions)
  this.viewer.Map.addControl(this.createTool)
}

Measure.prototype.setSegmentVisible = function (visible) {
  this.showSegments = visible
  this.layer.changed()
}

Measure.prototype.getSegmentVisible = function (visible) {
  return this.showSegments
}

Measure.prototype.clear = function (visible) {
  return this.layer.getSource().clear()
}

export default Measure
